import React, { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Star, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { services, paymentTypes, referralSources, googleReviews } from '../../data/content';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingModal = ({ isOpen, onClose, sourcePage = 'home' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    preferredDays: [],
    preferredTime: '',
    service: '',
    paymentType: '',
    referralSource: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [errors, setErrors] = useState({});
  const [submittedPaymentType, setSubmittedPaymentType] = useState('');

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const timeSlots = ['Morning (8AM-12PM)', 'Afternoon (12PM-3PM)', 'Late Afternoon (3PM-5PM)'];

  // Dynamic confirmation message based on payment type
  const getConfirmationMessage = () => {
    switch (submittedPaymentType) {
      case 'insurance':
        return {
          title: 'Request Received!',
          message: "Thank you for your consultation request. We'll verify your insurance coverage and contact you within 1 business day to confirm your appointment and discuss any copay information.",
          note: 'Please have your insurance card ready when we call.'
        };
      case 'self-pay':
        return {
          title: 'Request Received!',
          message: "Thank you for your consultation request. Our team will contact you within 1 business day to confirm your appointment.",
          note: "We'll discuss our self-pay rates and any available payment plans during our call."
        };
      case 'not_sure':
        return {
          title: 'Request Received!',
          message: "Thank you for your consultation request. Our team will contact you within 1 business day to help you understand your coverage options.",
          note: "If you have insurance information, please have it ready when we call so we can check your benefits."
        };
      default:
        return {
          title: 'Request Received!',
          message: "Thank you for your consultation request. Our team will contact you within 1 business day to confirm your appointment.",
          note: null
        };
    }
  };

  // Handle Escape key to close modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (formData.preferredDays.length === 0) newErrors.preferredDays = 'Select at least one day';
    if (!formData.preferredTime) newErrors.preferredTime = 'Select a preferred time';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/booking-request`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        preferred_days: formData.preferredDays,
        preferred_time: formData.preferredTime,
        service_interest: formData.service || null,
        payment_type: formData.paymentType || null,
        referral_source: formData.referralSource || null,
        source_page: sourcePage,
        message: formData.message || null,
      });

      console.log('Booking request submitted:', response.data);
      setSubmittedPaymentType(formData.paymentType);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Booking submission error:', error);
      setSubmitError(
        error.response?.data?.detail || 
        'Failed to submit request. Please try again or call us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDayToggle = (day) => {
    setFormData((prev) => ({
      ...prev,
      preferredDays: prev.preferredDays.includes(day)
        ? prev.preferredDays.filter((d) => d !== day)
        : [...prev.preferredDays, day],
    }));
    if (errors.preferredDays) {
      setErrors((prev) => ({ ...prev, preferredDays: null }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleClose = () => {
    setIsSubmitted(false);
    setSubmitError(null);
    setSubmittedPaymentType('');
    setFormData({
      name: '',
      phone: '',
      email: '',
      preferredDays: [],
      preferredTime: '',
      service: '',
      paymentType: '',
      referralSource: '',
      message: '',
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
          aria-label="Close modal"
        >
          <X size={20} />
        </button>

        {isSubmitted ? (
          /* Success State */
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <h3 className="heading-2 mb-2">{getConfirmationMessage().title}</h3>
            <p className="body-medium text-gray-600 mb-4">
              {getConfirmationMessage().message}
            </p>
            {getConfirmationMessage().note && (
              <p className="text-sm text-teal-700 bg-teal-50 p-3 rounded-lg mb-6">
                {getConfirmationMessage().note}
              </p>
            )}
            <button onClick={handleClose} className="btn-primary">
              Close
            </button>
            
            {/* Google Review CTA after successful booking */}
            {googleReviews.enabled && googleReviews.reviewsUrl && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-2">{googleReviews.ctaAfterSubmit}</p>
                <a 
                  href={googleReviews.reviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm"
                  data-testid="booking-success-google-review"
                >
                  <Star size={16} className="fill-current" />
                  {googleReviews.ctaLeave}
                  <ExternalLink size={14} />
                </a>
              </div>
            )}
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            <div className="mb-6">
              <h3 className="heading-2 mb-2">Request an Evaluation</h3>
              <p className="body-small text-gray-600">
                Fill out the form below and we'll contact you to schedule your appointment.
              </p>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 flex items-start gap-2">
                <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{submitError}</p>
              </div>
            )}

            {/* HIPAA Notice */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-6 flex items-start gap-2">
              <AlertCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800">
                <strong>Privacy Notice:</strong> This form collects contact information only. Please do not include sensitive medical details. Your information is kept confidential.
              </p>
            </div>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                  placeholder="Your full name"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Phone & Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-input ${errors.phone ? 'border-red-500' : ''}`}
                    placeholder="(555) 123-4567"
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="you@email.com"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
              </div>

              {/* Preferred Days */}
              <div>
                <label className="form-label">Preferred Days *</label>
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDayToggle(day)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        formData.preferredDays.includes(day)
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
                {errors.preferredDays && <p className="text-red-500 text-xs mt-1">{errors.preferredDays}</p>}
              </div>

              {/* Preferred Time */}
              <div>
                <label className="form-label">Preferred Time *</label>
                <div className="flex flex-wrap gap-2">
                  {timeSlots.map((time) => (
                    <button
                      key={time}
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, preferredTime: time }));
                        if (errors.preferredTime) {
                          setErrors((prev) => ({ ...prev, preferredTime: null }));
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        formData.preferredTime === time
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
                {errors.preferredTime && <p className="text-red-500 text-xs mt-1">{errors.preferredTime}</p>}
              </div>

              {/* Service Interest */}
              <div>
                <label className="form-label">Service of Interest</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="">Select a service (optional)</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment Type & Referral Source Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Payment Type */}
                <div>
                  <label className="form-label">Payment Method</label>
                  <select
                    name="paymentType"
                    value={formData.paymentType}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">Select (optional)</option>
                    {paymentTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Referral Source */}
                <div>
                  <label className="form-label">How did you hear about us?</label>
                  <select
                    name="referralSource"
                    value={formData.referralSource}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">Select (optional)</option>
                    {referralSources.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="form-label">Additional Notes</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-input form-textarea"
                  placeholder="Any additional information (optional)"
                  rows={3}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Request Evaluation'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
